<template>
  <div class="layout-container">
    <el-container>
      <el-header class="layout-header">
        <div class="layout-header-content">
          <div class="layout-left">
            <el-button
              link
              class="layout-menu-trigger layout-show-sm"
              aria-label="打开菜单"
              @click="mobileMenuOpen = true"
            >
              <svg
                class="layout-hamburger"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z"
                />
              </svg>
            </el-button>

            <div class="layout-logo" @click="$router.push('/')">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="layout-logo-icon"
              >
                <path
                  d="M12 2L2 7L12 12L22 7L12 2Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2 17L12 22L22 17"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2 12L12 17L22 12"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="layout-logo-text">XPZ社区</span>
            </div>

            <div class="layout-nav layout-hide-sm">
              <el-link
                v-for="item in NAV_ITEMS"
                :key="item.key"
                :type="activeIndex === item.path ? 'primary' : 'default'"
                underline="never"
                @click="handleNavClick(item)"
              >
                {{ item.label }}
              </el-link>
            </div>
          </div>

          <div class="layout-right">
            <div v-if="showGlobalSearch" class="layout-search-wrap layout-hide-sm">
              <u-search :config="globalSearchConfig" @submit="onSearchSubmit" />
            </div>
            <el-button
              v-if="showGlobalSearch"
              link
              class="layout-icon-btn layout-show-sm"
              aria-label="搜索"
              @click="goSearchPage"
            >
              <el-icon><Search /></el-icon>
            </el-button>

            <el-button
              type="primary"
              plain
              class="layout-publish layout-hide-sm"
              @click="handlePublishClick"
            >
              <el-icon><EditPen /></el-icon>
              <span>发帖子</span>
            </el-button>

            <el-badge
              :value="unreadCount"
              :max="99"
              :hidden="unreadCount <= 0"
              class="layout-notice"
            >
              <button
                type="button"
                class="layout-notice-btn"
                aria-label="消息中心"
                @click="handleNoticeCenterClick"
              >
                <el-icon :size="22"><Bell /></el-icon>
              </button>
            </el-badge>

            <el-dropdown v-if="isLogin" trigger="click" @command="handleUserCommand">
              <button
                type="button"
                class="layout-user"
                :aria-label="userInfo.nickName || userInfo.username || '用户菜单'"
              >
                <el-avatar :src="userInfo.avatar" :size="32">
                  {{ (userInfo.nickName || userInfo.username || "?").charAt(0) }}
                </el-avatar>
              </button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="profile"
                    ><el-icon><User /></el-icon>个人中心</el-dropdown-item
                  >
                  <el-dropdown-item divided command="logout"
                    ><el-icon><SwitchButton /></el-icon>退出登录</el-dropdown-item
                  >
                </el-dropdown-menu>
              </template>
            </el-dropdown>

            <button
              v-else
              type="button"
              class="layout-user layout-user--guest"
              aria-label="登录"
              @click="openLoginDialog('login')"
            >
              <el-avatar :size="32" class="layout-guest-avatar">
                <el-icon :size="18"><User /></el-icon>
              </el-avatar>
            </button>
          </div>
        </div>
      </el-header>

      <el-main
        class="layout-main"
        :class="{
          'layout-main--fab-pad': !isOauthCallback,
          'layout-main--oauth': isOauthCallback,
        }"
      >
        <div
          class="layout-main-content"
          :class="{ 'layout-main-content--oauth': isOauthCallback }"
        >
          <router-view />
        </div>
      </el-main>

      <el-footer class="layout-footer">
        <div class="layout-footer-content">
          <p>&copy; 2026 XPZ社区. All rights reserved.</p>
          <p>
            <el-link href="https://beian.miit.gov.cn/" target="_blank" underline="never"
              >皖ICP备2026000978号-1</el-link
            >
          </p>
        </div>
      </el-footer>
    </el-container>

    <LoginDialog
      v-model="loginDialogVisible"
      :default-tab="loginDialogTab"
      @success="handleAuthSuccess"
    />

    <PostDetailDialog
      v-model="postDetailVisible"
      :post-id="postDetailId"
      @need-login="openLoginDialog('login')"
      @closed="onPostDetailClosed"
      @deleted="onPostDetailDeleted"
    />

    <el-drawer
      v-model="publishDrawerVisible"
      direction="btt"
      size="200px"
      :with-header="false"
      append-to-body
      destroy-on-close
      class="publish-composer-drawer"
      body-class="publish-composer-drawer-body"
    >
      <PostComposer
        :topics="publishTopics"
        compact
        @need-login="onPublishNeedLogin"
        @published="onPublishSuccess"
      />
    </el-drawer>

    <el-drawer
      v-model="mobileMenuOpen"
      direction="ltr"
      title="菜单"
      size="280px"
      body-class="layout-drawer-body"
    >
      <nav class="layout-drawer-nav" aria-label="主导航">
        <button
          v-for="item in NAV_ITEMS"
          :key="item.key"
          type="button"
          class="layout-drawer-link"
          :class="{ 'is-active': activeIndex === item.path }"
          @click="goNavAndCloseMenu(item)"
        >
          {{ item.label }}
        </button>
      </nav>
    </el-drawer>

    <el-button
      v-if="!isOauthCallback"
      class="layout-fab layout-show-sm"
      type="primary"
      circle
      aria-label="发帖子"
      @click="handlePublishClick"
    >
      <el-icon :size="20"><EditPen /></el-icon>
    </el-button>

    <SupportFloat v-if="!isOauthCallback" />
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import { Bell, EditPen, Search, SwitchButton, User } from "@element-plus/icons-vue";
import { getCurrentUser } from "@/api/user";
import { getUnreadCountTotal } from "@/api/notice";
import { getTopicAll } from "@/api/post";
import { clearAuth, getToken } from "@/utils/authStorage";
import { scrollToPostComposer } from "@/utils/scroll";
import LoginDialog from "./LoginDialog.vue";
import PostDetailDialog from "./post/PostDetailDialog.vue";
import PostComposer from "./post/PostComposer.vue";
import SupportFloat from "./SupportFloat.vue";

const BUILTIN_SEARCH_KEYWORDS = ["Skill", "Agent", "VibeCoding", "大模型", "AI工具"];

const NAV_ITEMS = [
  { key: "home", path: "/", label: "主页" },
  { key: "tools", path: null, label: "工具", external: "https://tools.xpz01.cn/" },
  { key: "docs", path: null, label: "文档", external: "https://docs.xpz01.cn/" },
];

export default {
  name: "AppLayout",
  components: {
    LoginDialog,
    PostDetailDialog,
    PostComposer,
    SupportFloat,
    Bell,
    EditPen,
    Search,
    SwitchButton,
    User,
  },
  data() {
    return {
      isLogin: false,
      userInfo: {},
      loginDialogVisible: false,
      loginDialogTab: "login",
      mobileMenuOpen: false,
      unreadCount: 0,
      noticeSocket: null,
      postDetailVisible: false,
      postDetailId: null,
      postDetailFromRoute: false,
      publishDrawerVisible: false,
      publishTopics: [],
      globalSearchConfig: {
        search: "",
        keywords: [...BUILTIN_SEARCH_KEYWORDS],
        hotSearchList: [...BUILTIN_SEARCH_KEYWORDS],
      },
    };
  },
  provide() {
    const vm = this;
    return {
      layoutRef: {
        openLoginDialog: this.openLoginDialog,
        openPostDetail: this.openPostDetail,
        openPublishComposer: this.openPublishComposer,
        loadUserInfo: this.loadUserInfo,
        get userInfo() {
          return vm.userInfo;
        },
        get isLogin() {
          return vm.isLogin;
        },
      },
    };
  },
  computed: {
    NAV_ITEMS: () => NAV_ITEMS,
    activeIndex() {
      const path = this.$route.path;
      if (path === "/" || /^\/post(\/|$)/.test(path)) return "/";
      return path;
    },
    showGlobalSearch() {
      return !this.$route.path.startsWith("/search");
    },
    isOauthCallback() {
      return this.$route.path.startsWith("/oauth/callback");
    },
  },
  watch: {
    "$route.fullPath"() {
      this.mobileMenuOpen = false;
      if (!this.$route.path.startsWith("/search")) {
        const q = this.$route.query.q;
        this.globalSearchConfig.search = typeof q === "string" ? q : "";
      }
      this.syncLoginState();
      this.syncPostDetailRoute();
    },
  },
  mounted() {
    this.syncLoginState();
    this.syncPostDetailRoute();
    if (getToken()) {
      this.loadUserInfo();
      this.loadUnreadCountTotal();
      this.initNoticeSocket();
    }

    this._loginListener = (e) => {
      if (e.detail?.isLogin === true) this.checkLogin();
    };
    this._noticeListener = () => this.loadUnreadCountTotal();
    this._noticeSyncListener = (e) => {
      const count = e.detail?.unreadCount;
      if (typeof count === "number") this.unreadCount = count;
    };
    window.addEventListener("login-state-change", this._loginListener);
    window.addEventListener("notice-changed", this._noticeListener);
    window.addEventListener("notice-unread-sync", this._noticeSyncListener);
  },
  beforeUnmount() {
    window.removeEventListener("login-state-change", this._loginListener);
    window.removeEventListener("notice-changed", this._noticeListener);
    window.removeEventListener("notice-unread-sync", this._noticeSyncListener);
    this.closeNoticeSocket();
  },
  methods: {
    /* ---- 登录态 ---- */
    syncLoginState() {
      this.isLogin = !!getToken();
      if (!this.isLogin) this.userInfo = {};
    },
    async loadUserInfo() {
      try {
        const res = await getCurrentUser();
        if (res.data.code === 200) {
          this.userInfo = res.data.data;
        } else {
          clearAuth();
          this.isLogin = false;
          this.userInfo = {};
        }
      } catch (e) {
        console.error("获取用户信息失败:", e);
        if (e.response?.status === 401) {
          clearAuth();
          this.isLogin = false;
          this.userInfo = {};
        }
      }
    },
    checkLogin() {
      if (getToken()) {
        this.isLogin = true;
        this.loadUserInfo();
        this.loadUnreadCountTotal();
        this.initNoticeSocket();
      } else {
        this.isLogin = false;
        this.userInfo = {};
        this.unreadCount = 0;
        this.closeNoticeSocket();
      }
    },
    logout() {
      clearAuth();
      this.isLogin = false;
      this.userInfo = {};
      this.unreadCount = 0;
      this.closeNoticeSocket();
      ElMessage.success("已退出登录");
      window.dispatchEvent(
        new CustomEvent("login-state-change", { detail: { isLogin: false } })
      );
      window.dispatchEvent(new StorageEvent("storage", { key: "token", newValue: null }));
      if (this.$route.path !== "/") this.$router.push("/");
    },
    openLoginDialog(tab = "login") {
      this.loginDialogTab = tab;
      this.loginDialogVisible = true;
    },
    openPostDetail(postId, { fromRoute = false } = {}) {
      if (postId == null || postId === "") return;
      const id = Number(postId);
      if (!Number.isFinite(id) || id <= 0) return;
      this.postDetailId = id;
      this.postDetailFromRoute = fromRoute;
      this.postDetailVisible = true;
    },
    syncPostDetailRoute() {
      const match = this.$route.path.match(/^\/post\/(\d+)$/);
      if (match) {
        this.openPostDetail(match[1], { fromRoute: true });
      }
    },
    onPostDetailClosed() {
      if (!this.postDetailFromRoute) {
        this.postDetailId = null;
        return;
      }
      this.postDetailFromRoute = false;
      this.postDetailId = null;
      if (/^\/post\/\d+$/.test(this.$route.path)) {
        if (window.history.length > 1) this.$router.back();
        else this.$router.replace("/");
      }
    },
    onPostDetailDeleted() {
      window.dispatchEvent(new CustomEvent("post-deleted"));
    },
    handleAuthSuccess(type) {
      if (type === "login") {
        this.isLogin = true;
        this.loadUserInfo();
        this.loadUnreadCountTotal();
        this.initNoticeSocket();
      }
    },
    handleUserCommand(command) {
      if (command === "profile") this.$router.push("/profile");
      else if (command === "logout") this.logout();
    },

    /* ---- 消息 ---- */
    async loadUnreadCountTotal() {
      if (!getToken()) {
        this.unreadCount = 0;
        return;
      }
      try {
        const res = await getUnreadCountTotal();
        if (res.data?.code === 200) this.unreadCount = Number(res.data.data || 0);
      } catch (e) {
        console.error("加载未读消息数失败:", e);
      }
    },
    initNoticeSocket() {
      const token = getToken();
      if (!token) return;
      this.closeNoticeSocket();
      try {
        const apiBase = (
          process.env.VUE_APP_API_BASE_URL || window.location.origin
        ).replace(/\/+$/, "");
        const wsBase = apiBase
          .replace(/^https:\/\//, "wss://")
          .replace(/^http:\/\//, "ws://");
        const ws = new WebSocket(
          `${wsBase}/api/ws/notice?token=${encodeURIComponent(token)}`
        );
        ws.onmessage = (evt) => {
          try {
            const payload = JSON.parse(evt.data || "{}");
            if (payload.event === "xpz_notice_unread") {
              this.unreadCount = Number(payload.data?.unreadCount || 0);
            }
          } catch (e) {
            console.error("消息中心数据解析失败:", e);
          }
        };
        ws.onclose = () => {
          this.noticeSocket = null;
        };
        this.noticeSocket = ws;
      } catch (e) {
        console.error("消息中心连接失败:", e);
      }
    },
    closeNoticeSocket() {
      if (this.noticeSocket) {
        try {
          this.noticeSocket.close();
        } catch (e) {
          console.error(e);
        }
        this.noticeSocket = null;
      }
    },

    /* ---- 其他 ---- */
    isMobileLayout() {
      return (
        typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
      );
    },
    async openPublishComposer() {
      if (!getToken()) {
        this.openLoginDialog("login");
        return;
      }
      await this.ensurePublishTopics();
      this.publishDrawerVisible = true;
    },
    async ensurePublishTopics() {
      if (this.publishTopics.length) return;
      try {
        const res = await getTopicAll();
        if (res?.data?.code === 200) {
          this.publishTopics = res.data.data || [];
        }
      } catch (e) {
        console.error("加载话题失败:", e);
      }
    },
    onPublishNeedLogin() {
      this.publishDrawerVisible = false;
      this.openLoginDialog("login");
    },
    onPublishSuccess() {
      this.publishDrawerVisible = false;
      window.dispatchEvent(new CustomEvent("post-published"));
      if (this.$route.path !== "/") {
        this.$router.push("/");
      }
    },
    handlePublishClick() {
      if (!getToken()) {
        this.openLoginDialog("login");
        return;
      }
      // 手机端：悬浮按钮打开底部发布弹窗
      if (this.isMobileLayout()) {
        this.openPublishComposer();
        return;
      }
      // 网页端：定位到主页发布框（扣除吸顶导航高度）
      if (this.$route.path === "/") {
        this.$nextTick(() => scrollToPostComposer());
        return;
      }
      this.$router.push({ path: "/", hash: "#compose" });
    },
    handleNoticeCenterClick() {
      if (getToken()) this.$router.push("/notice");
      else this.openLoginDialog("login");
    },
    onSearchSubmit(val) {
      const keyword = (val || "").trim();
      if (keyword) this.$router.push({ path: "/search", query: { q: keyword } });
    },
    goSearchPage() {
      this.$router.push("/search");
    },
    handleNavClick(item) {
      if (item?.tip) {
        ElMessage.info(item.tip);
        return;
      }
      if (item?.external) {
        window.open(item.external, "_blank", "noopener,noreferrer");
        return;
      }
      if (item?.path) this.$router.push(item.path);
    },
    goNavAndCloseMenu(item) {
      this.mobileMenuOpen = false;
      this.handleNavClick(item);
    },
  },
};
</script>

<style scoped>
/* ---- 响应式工具 ---- */
.layout-show-sm {
  display: none !important;
}

@media (max-width: 768px) {
  .layout-hide-sm {
    display: none !important;
  }
  .layout-show-sm {
    display: inline-flex !important;
  }
}

/* ---- 整体结构 ---- */
.layout-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.layout-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000;
  height: 60px;
  padding: 0;
  background-color: var(--el-bg-color);
  border-bottom: 1px solid var(--el-border-color-light);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.layout-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.layout-left {
  display: flex;
  align-items: center;
  gap: 30px;
}

.layout-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.layout-right :deep(.el-button + .el-button) {
  margin-left: 0;
}

/* ---- Logo ---- */
.layout-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.layout-logo-icon {
  width: 28px;
  height: 28px;
  color: var(--el-color-primary);
}

.layout-logo-text {
  font-size: 18px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

/* ---- Nav ---- */
.layout-nav {
  display: flex;
  gap: 24px;
}

/* ---- Search ---- */
.layout-search-wrap {
  flex: 1;
  min-width: 0;
}

/* ---- Icon button (移动端：汉堡/搜索/登录) ---- */
.layout-menu-trigger,
.layout-icon-btn {
  flex-shrink: 0;
  padding: 4px 8px;
  color: var(--el-text-color-primary);
}

.layout-menu-trigger:hover,
.layout-icon-btn:hover {
  color: var(--el-color-primary);
}

.layout-hamburger {
  display: block;
  width: 22px;
  height: 22px;
}

/* ---- Notice：无边框大图标 + 角标 ---- */
.layout-notice {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.layout-notice :deep(.el-badge__content) {
  transform: translateY(-50%) translateX(50%);
  border: none;
}

.layout-notice-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--el-text-color-secondary);
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.layout-notice-btn:hover {
  color: var(--el-color-primary);
  background: var(--el-fill-color-light);
}

.layout-notice-btn:focus-visible {
  outline: 2px solid var(--el-color-primary);
  outline-offset: 2px;
}

/* ---- User ---- */
.layout-user {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 50%;
  line-height: 0;
}

.layout-user:focus-visible {
  outline: 2px solid var(--el-color-primary);
  outline-offset: 2px;
}

.layout-guest-avatar {
  background: var(--el-fill-color);
  color: var(--el-text-color-secondary);
}

/* ---- Main ---- */
.layout-main {
  flex: 1;
  padding: 75px 0 15px;
  min-height: calc(100vh - 130px);
  background-color: var(--el-bg-color-page);
  /* 覆盖 el-main 默认 overflow:auto，否则 position:sticky 无法相对视口贴顶 */
  overflow: visible;
}

@media (max-width: 768px) {
  .layout-main--fab-pad {
    padding-bottom: calc(24px + 56px + env(safe-area-inset-bottom, 0px));
  }
}

.layout-main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.layout-main--oauth {
  padding-top: 68px;
  padding-bottom: 8px;
  min-height: 0;
}

.layout-main-content--oauth {
  max-width: none;
  padding: 0;
}

/* ---- Footer ---- */
.layout-footer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  color: var(--el-text-color-secondary);
  padding: 10px 0;
}

/* ---- FAB ---- */
.layout-fab {
  position: fixed;
  right: max(16px, env(safe-area-inset-right, 0px));
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  z-index: 1990;
  width: 52px !important;
  min-width: 52px !important;
  height: 52px !important;
  padding: 0;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
}

/* ---- Drawer ---- */
.layout-drawer-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.layout-drawer-link {
  display: block;
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 15px;
  color: var(--el-text-color-primary);
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.layout-drawer-link:hover {
  background: var(--el-fill-color-light);
}

.layout-drawer-link.is-active {
  color: var(--el-color-primary);
  font-weight: 600;
}

/* ---- 移动端覆写 ---- */
@media (max-width: 768px) {
  .layout-header-content {
    padding: 0 12px;
  }
  .layout-left {
    gap: 10px;
    min-width: 0;
  }
  .layout-right {
    gap: 8px;
    flex-shrink: 0;
  }
  .layout-main-content {
    padding: 0 12px;
  }
  .layout-logo-text {
    font-size: 16px;
  }
}
</style>

<style>
.publish-composer-drawer.el-drawer {
  border-radius: 16px 16px 0 0;
  height: auto !important;
  max-height: 50vh;
}

.publish-composer-drawer .el-drawer__body {
  flex: none !important;
  padding: 8px 14px calc(20px + env(safe-area-inset-bottom, 0px));
  overflow: visible;
}

.publish-composer-drawer-body {
  padding: 0;
}

.publish-composer-drawer .post-composer {
  margin: 0 !important;
  padding: 0 !important;
  min-height: 0 !important;
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
  border-radius: 0 !important;
}

.publish-composer-drawer .post-composer::before {
  content: "";
  display: block;
  width: 36px;
  height: 4px;
  margin: 2px auto 10px;
  border-radius: 2px;
  background: var(--el-border-color);
}

.publish-composer-drawer .composer-input .el-textarea__inner {
  height: auto !important;
  min-height: 72px !important;
  padding: 10px 12px 24px !important;
}
</style>
